import {
  ArrowLeftRight,
  Banknote,
  BellRing,
  Bot,
  CalendarClock,
  CarFront,
  ChartColumn,
  ChartPie,
  ClipboardCheck,
  ExternalLink,
  Gem,
  Hammer,
  Heart,
  MessageCircle,
  Monitor,
  MonitorSmartphone,
  Star,
  Truck,
  Users,
  Wallet,
  Warehouse,
  Wrench,
  Zap,
} from 'lucide-react';

// Conteúdo REAL da landing (somente o que já existia em produção + os cards
// "Inventário" e "Próximos serviços" aprovados no protótipo v2). Planos e links de
// navegação continuam fora, como estavam comentados no código anterior.

// Canal do "Falar com um consultor": abre o WhatsApp Web/app do consultor
// comercial em nova aba. Não existe outro canal de contato no projeto.
export const WHATSAPP_CONTACT_URL = `https://wa.me/5519994846675?text=${encodeURIComponent('Quero conhecer o FlowVendas ERP')}`;

export const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Inter:wght@400;500;600;700&display=swap';

export const FEATURE_GROUPS = [
  {
    num: '01',
    title: 'Vendas e operações',
    color: 'var(--blue)',
    items: [
      { Icon: Monitor, title: 'Frente de caixa', text: 'Realize vendas de forma rápida e eficiente, com interface intuitiva que acelera o atendimento.' },
      { Icon: Banknote, title: 'Controle de caixa', text: 'Controle total das entradas e saídas, com registro automático de cada transação.' },
      { Icon: ArrowLeftRight, title: 'Fluxo de caixa', text: 'Projeções que ajudam a visualizar o futuro financeiro e decidir com mais confiança.' },
    ],
  },
  {
    num: '02',
    title: 'Estoque e produtos',
    color: 'var(--green)',
    items: [
      { Icon: Warehouse, title: 'Controle de estoque', text: 'Indicadores que garantem que você nunca fique sem os produtos essenciais.' },
      { Icon: Truck, title: 'Cadastro de fornecedores', text: 'Organize sua rede de fornecedores para compras e negociações mais eficientes.' },
      { Icon: ClipboardCheck, title: 'Inventário', text: 'Faça a contagem física dos produtos e corrija as divergências com o estoque do sistema.' },
    ],
  },
  {
    num: '03',
    title: 'Serviços e manutenção',
    color: 'var(--orange)',
    items: [
      { Icon: Wrench, title: 'Ordem de serviço', text: 'Acompanhe cada trabalho em tempo real e mantenha o cliente sempre informado.' },
      { Icon: Hammer, title: 'Cadastro de serviços', text: 'Catálogo de serviços com checklists personalizados que padronizam a qualidade.' },
      { Icon: CarFront, title: 'Cadastro de veículos', text: 'Controle da frota com histórico completo de serviços e manutenções.' },
      { Icon: ClipboardCheck, title: 'Checklist de inspeção veicular', text: 'Documente condições, defeitos e manutenções de forma organizada e profissional.' },
      { Icon: CalendarClock, title: 'Próximos serviços', text: 'Serviços e revisões recorrentes a refazer, com base no histórico de ordens de serviço.' },
    ],
  },
  {
    num: '04',
    title: 'Clientes e relacionamento',
    color: 'var(--indigo)',
    items: [
      { Icon: Users, title: 'Cadastro de clientes', text: 'Base completa com histórico de compras e serviços para um atendimento personalizado.' },
      { Icon: Gem, title: 'Orçamentos', text: 'Crie propostas profissionais e gere ordens de serviço a partir delas com mais agilidade.' },
      { Icon: ExternalLink, title: 'Portal do cliente', text: 'O cliente abre o link do orçamento, confere itens e valores e aprova com um clique, gerando a ordem de serviço na hora. Se tiver dúvida, envia direto ao lojista.' },
    ],
  },
  {
    num: '05',
    title: 'Análise e relatórios',
    color: 'var(--violet)',
    items: [
      { Icon: ChartColumn, title: 'Relatórios e gráficos', text: 'Relatórios customizáveis que revelam insights sobre o desempenho do negócio.' },
      { Icon: ChartPie, title: 'Dashboards', text: 'Painéis interativos com as principais métricas para acompanhar resultados em tempo real.' },
    ],
  },
  {
    // Grupo 06 é destacado na seção escura "Inteligência e WhatsApp".
    num: '06',
    title: 'Inteligência e WhatsApp',
    color: 'var(--pink)',
    items: [
      { Icon: Bot, title: 'Assistente de IA', text: 'Pergunte em linguagem natural sobre vendas, estoque e clientes e receba respostas direto do seu sistema. O assistente só consulta, nunca altera dados.' },
      { Icon: MessageCircle, title: 'Ordens e orçamentos no WhatsApp', text: 'Envie a ordem de serviço em PDF e o orçamento ao cliente por WhatsApp, direto do sistema, sem sair da tela.' },
      { Icon: BellRing, title: 'Lembrete de próximos serviços', text: 'O cliente é avisado por WhatsApp antes da revisão ou do próximo serviço do veículo, com a antecedência que você definir. Também dá para enviar manualmente.' },
    ],
  },
];

export const HIGHLIGHTS = [
  { Icon: MonitorSmartphone, title: 'Acesso de qualquer lugar', text: '100% na nuvem, sem instalar nada: acompanhe caixa, estoque e clientes do computador da loja ou do celular.' },
  { Icon: Star, title: 'Gestão integrada', text: 'Venda, estoque, ordens de serviço e financeiro conversam entre si — sem planilha paralela.' },
  { Icon: Zap, title: 'Rotina mais rápida', text: 'Interface pensada para reduzir cliques no balcão e agilizar o atendimento nos horários de pico.' },
  { Icon: Heart, title: 'Relacionamento contínuo', text: 'Histórico completo de compras e serviços por cliente para um atendimento mais próximo.' },
];

export const FLOW_NODES = [
  { key: 'venda', label: 'Venda', color: 'var(--blue)', Icon: Monitor, top: '6%', left: '50%', delay: undefined },
  { key: 'estoque', label: 'Estoque', color: 'var(--green)', Icon: Warehouse, top: '34%', left: '95%', delay: '-1.2s' },
  { key: 'financeiro', label: 'Financeiro', color: 'var(--orange)', Icon: Wallet, top: '78%', left: '78%', delay: '-2.4s' },
  { key: 'clientes', label: 'Clientes', color: 'var(--indigo)', Icon: Users, top: '78%', left: '22%', delay: '-3.6s' },
  { key: 'relatorios', label: 'Relatórios', color: 'var(--violet)', Icon: ChartColumn, top: '34%', left: '5%', delay: '-4.8s' },
];

export const HERO_PILLS = ['ERP na nuvem', 'Sem instalação', 'Frente de caixa', 'Ordens de serviço', 'Estoque', 'Financeiro', 'Clientes'];

export const ROTATING_WORDS = ['Vendas', 'Estoque', 'Ordens de serviço', 'Financeiro', 'Clientes'];

export const ASK_PLACEHOLDER = ['Pergunte sobre vendas, estoque e clientes…'];
